import React, { Fragment } from 'react';
import { filter, includes, isNil } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import SearchBox from '../../../../components/outline/SearchBox';
import BaseForm from '../../../../components/form/BaseForm';
import ButtonList from '../../../../components/ButtonList';
import InputNumber from '../../../../components/input/InputNumber';
import Select from '../../../../components/input/Select';
import CheckBox from '../../../../components/input/CheckBox';
import { LIST_PAGINATION_MODEL } from '../../../../store/modules/app/models';
import {
  INITIAL_PRODUCT_LIST_SEARCH_DATA,
  PRODUCT_LIST_SEARCH_DATA_MODEL,
} from '../../../../store/modules/product/models';
import {
  PRODUCT_DISCOUNT_BUTTON_LIST,
  PRODUCT_MAXIMUM_DISCOUNT_RATE,
  PRODUCT_MAXIMUM_PRICE,
  PRODUCT_SEARCH_PRICE_CHECK_BOX_LIST,
  PRODUCT_SEARCH_PRICE_LIST,
  PRODUCT_SEARCH_PRICE_SELECT_LIST,
  PRODUCT_SEARCH_RATE_CHECK_BOX_LIST,
} from '../../../../constants/service/app/productConstants';
import {
  setEndProductValueRange,
  setPriceRangeOnCheckBox,
  setRateRangeOnCheckBox,
  setStartProductValueRange,
} from '../../../../lib/utils/productUtils';
import { productActions } from '../../../../store/modules/product';
import './ProductListSearchBox.scss';

const ProductListSearchBox = ({ getList }) => {
  const dispatch = useDispatch();
  const { productList, categorySelectList } = useSelector((state) => state.product);
  const { searchData, tagGroupList, tagList, tagSearchList } = productList;

  const setDataFn = (data) => dispatch(productActions.setData(data));
  const setData = (data) =>
    setDataFn({
      path: ['productList', 'searchData', ...data.path],
      value: data.value,
    });

  const searchProductList = () => {
    getList({
      /**
       *  검색 시 UI 에서 사용되던 데이터를 제거하고 서버에 요청한다.
       */
      searchOption: {
        /**
         *  실판매가
         */
        salesPrice: {
          startPrice: salesPrice.startPrice,
          endPrice: salesPrice.endPrice,
        },
        /**
         *  판매가
         */
        originPrice: {
          startPrice: originPrice.startPrice,
          endPrice: originPrice.endPrice,
        },
        /**
         *  할인된 판매가
         */
        discountedPrice: {
          startPrice: discountedPrice.startPrice,
          endPrice: discountedPrice.endPrice,
        },
        /**
         *  할인율
         */
        discountRate: {
          startRate: discountRate.startRate,
          endRate: discountRate.endRate,
        },
        /**
         *  할인가
         */
        discountPrice: {
          startPrice: discountPrice.startPrice,
          endPrice: discountPrice.endPrice,
        },
        /**
         *  할인설정 (원, %)
         */
        discountType: discountType.value,
        /**
         *  상품 카테고리
         */
        productCategory: {
          list: categorySelectList.filter((item) => productCategory.list.find((id) => item.id === id)),
        },
        /**
         *  상품 태그
         */
        productTag,
      },
      pagination: LIST_PAGINATION_MODEL,
      tagSearchList,
    });
  };

  const searchAllProductList = () => {
    getList(INITIAL_PRODUCT_LIST_SEARCH_DATA);
    setData({
      path: [],
      value: PRODUCT_LIST_SEARCH_DATA_MODEL,
    });
  };

  const {
    priceDetailToggle,
    discountDetailToggle,
    originPrice,
    discountPrice,
    discountRate,
    discountType,
    discountedPrice,
    salesPrice,
    productTag,
    productCategory,
  } = searchData;

  return (
    <SearchBox className="product-list-search-box" onSearch={searchProductList} onClear={searchAllProductList}>
      <BaseForm
        label={
          <div className="search-label-wrap">
            <div>실판매가</div>

            {/*toggle 아이콘*/}
            <div className="detail-search-toggle-icon-wrap">
              {priceDetailToggle ? (
                <IoIosArrowUp
                  onClick={() =>
                    /**
                     *  판매가 상세 검색 시 데이터 초기화
                     */
                    setData({
                      path: [],
                      value: {
                        ...searchData,
                        priceDetailToggle: false,
                        originPrice: PRODUCT_LIST_SEARCH_DATA_MODEL.originPrice,
                        discountedPrice: PRODUCT_LIST_SEARCH_DATA_MODEL.discountedPrice,
                      },
                    })
                  }
                />
              ) : (
                <IoIosArrowDown
                  onClick={() =>
                    /**
                     *  판매가 상세 검색 시 데이터 초기화
                     */
                    setData({
                      path: [],
                      value: {
                        ...searchData,
                        priceDetailToggle: true,
                        originPrice: PRODUCT_LIST_SEARCH_DATA_MODEL.originPrice,
                        discountedPrice: PRODUCT_LIST_SEARCH_DATA_MODEL.discountedPrice,
                      },
                    })
                  }
                />
              )}
            </div>
          </div>
        }
        content={
          <Fragment>
            {/*범위로 검색*/}
            <div className="check-search-wrap">
              {PRODUCT_SEARCH_PRICE_CHECK_BOX_LIST.map((item) => (
                <CheckBox
                  key={item.id}
                  checked={includes(salesPrice.checkList, item.id)}
                  text={item.name}
                  onChange={(checked) => {
                    const { checkList, startPrice, endPrice } = setPriceRangeOnCheckBox(checked, item, salesPrice);

                    setData({
                      path: ['salesPrice'],
                      value: {
                        checkList,
                        startPrice,
                        endPrice,
                      },
                    });
                  }}
                />
              ))}
            </div>

            {/*직접 입력*/}
            {includes(salesPrice.checkList, PRODUCT_SEARCH_PRICE_CHECK_BOX_LIST.length) ? (
              <div className="input-search-wrap">
                <InputNumber
                  value={salesPrice.startPrice[0]}
                  placeholder={0}
                  min={0}
                  max={PRODUCT_MAXIMUM_PRICE - 1}
                  onChange={(value) =>
                    setData({
                      path: ['salesPrice', 'startPrice'],
                      value: [setStartProductValueRange(value, salesPrice.endPrice[0])],
                    })
                  }
                  addonAfter={'원'}
                />

                <span className="dash">~</span>

                <InputNumber
                  value={salesPrice.endPrice[0]}
                  placeholder={0}
                  min={9999}
                  max={PRODUCT_MAXIMUM_PRICE}
                  onChange={(value) => {
                    setData({
                      path: ['salesPrice', 'endPrice'],
                      value: [setEndProductValueRange(value, salesPrice.startPrice[0], PRODUCT_MAXIMUM_PRICE)],
                    });
                  }}
                  addonAfter={'원'}
                />
              </div>
            ) : null}
          </Fragment>
        }
      />
      {priceDetailToggle ? (
        <Fragment>
          <BaseForm
            label={'판매가'}
            content={
              <div className="select-search-wrap">
                {/*범위 검색*/}
                <Select
                  className="select-search-option"
                  value={originPrice.priceIndex}
                  list={PRODUCT_SEARCH_PRICE_SELECT_LIST}
                  onChange={(value) => {
                    const priceData = filter(PRODUCT_SEARCH_PRICE_LIST, (item) => item.id === value)[0];

                    setData({
                      path: ['originPrice'],
                      value: {
                        priceIndex: priceData.id,
                        startPrice: priceData.id ? priceData.value.startPrice : null,
                        endPrice: priceData.id ? priceData.value.endPrice : null,
                      },
                    });
                  }}
                />

                {/*직접 입력*/}
                {originPrice.priceIndex === 6 ? (
                  <div className="input-search-wrap">
                    <InputNumber
                      value={originPrice.startPrice}
                      placeholder={0}
                      min={0}
                      max={PRODUCT_MAXIMUM_PRICE - 1}
                      onChange={(value) => {
                        setData({
                          path: ['originPrice', 'startPrice'],
                          value: setStartProductValueRange(value, originPrice.endPrice),
                        });
                      }}
                      addonAfter={'원'}
                    />

                    <span className="dash">~</span>

                    <InputNumber
                      value={originPrice.endPrice}
                      placeholder={0}
                      min={9999}
                      max={PRODUCT_MAXIMUM_PRICE}
                      onChange={(value) => {
                        setData({
                          path: ['originPrice', 'endPrice'],
                          value: setEndProductValueRange(value, originPrice.startPrice, PRODUCT_MAXIMUM_PRICE),
                        });
                      }}
                      addonAfter={'원'}
                    />
                  </div>
                ) : null}
              </div>
            }
          />

          <BaseForm
            label={'할인된 판매가'}
            content={
              <div className="select-search-wrap">
                <Select
                  value={discountedPrice.priceIndex}
                  list={PRODUCT_SEARCH_PRICE_SELECT_LIST}
                  onChange={(value) => {
                    const priceData = filter(PRODUCT_SEARCH_PRICE_LIST, (item) => item.id === value)[0];

                    setData({
                      path: ['discountedPrice'],
                      value: {
                        priceIndex: priceData.id,
                        startPrice: priceData.id ? priceData.value.startPrice : null,
                        endPrice: priceData.id ? priceData.value.endPrice : null,
                      },
                    });
                  }}
                />

                {/*직접 입력*/}
                {discountedPrice.priceIndex === 6 ? (
                  <div className="input-search-wrap">
                    <InputNumber
                      value={discountedPrice.startPrice}
                      placeholder={0}
                      min={0}
                      max={PRODUCT_MAXIMUM_PRICE - 1}
                      onChange={(value) => {
                        setData({
                          path: ['discountedPrice', 'startPrice'],
                          value: setStartProductValueRange(value, discountedPrice.endPrice),
                        });
                      }}
                      addonAfter={'원'}
                    />

                    <span className="dash">~</span>

                    <InputNumber
                      value={discountedPrice.endPrice}
                      placeholder={0}
                      min={9999}
                      max={PRODUCT_MAXIMUM_PRICE}
                      onChange={(value) => {
                        setData({
                          path: ['discountedPrice', 'endPrice'],
                          value: setEndProductValueRange(value, discountedPrice.startPrice, PRODUCT_MAXIMUM_PRICE),
                        });
                      }}
                      addonAfter={'원'}
                    />
                  </div>
                ) : null}
              </div>
            }
          />
        </Fragment>
      ) : null}

      <BaseForm
        label={
          <div className="search-label-wrap">
            <div>할인율</div>

            {/*toggle 아이콘*/}
            <div className="detail-search-toggle-icon-wrap">
              {discountDetailToggle ? (
                <IoIosArrowUp
                  onClick={() =>
                    /**
                     *  판매가 상세 검색 시 데이터 초기화
                     */
                    setData({
                      path: [],
                      value: {
                        ...searchData,
                        discountDetailToggle: false,
                        discountPrice: PRODUCT_LIST_SEARCH_DATA_MODEL.discountPrice,
                        discountType: PRODUCT_LIST_SEARCH_DATA_MODEL.discountType,
                      },
                    })
                  }
                />
              ) : (
                <IoIosArrowDown
                  onClick={() =>
                    /**
                     *  판매가 상세 검색 시 데이터 초기화
                     */
                    setData({
                      path: [],
                      value: {
                        ...searchData,
                        discountDetailToggle: true,
                        discountPrice: PRODUCT_LIST_SEARCH_DATA_MODEL.discountPrice,
                        discountType: PRODUCT_LIST_SEARCH_DATA_MODEL.discountType,
                      },
                    })
                  }
                />
              )}
            </div>
          </div>
        }
        content={
          <Fragment>
            {/*범위로 검색*/}
            <div className="check-search-wrap">
              {PRODUCT_SEARCH_RATE_CHECK_BOX_LIST.map((item) => (
                <CheckBox
                  key={item.id}
                  checked={includes(discountRate.checkList, item.id)}
                  text={item.name}
                  onChange={(checked) => {
                    const { checkList, startRate, endRate } = setRateRangeOnCheckBox(checked, item, discountRate);

                    setData({
                      path: ['discountRate'],
                      value: {
                        checkList,
                        startRate,
                        endRate,
                      },
                    });
                  }}
                />
              ))}
            </div>

            {/*직접 입력*/}
            {includes(discountRate.checkList, PRODUCT_SEARCH_RATE_CHECK_BOX_LIST.length) ? (
              <div className="input-search-wrap">
                <InputNumber
                  value={discountRate.startRate[0]}
                  placeholder={0}
                  min={0}
                  max={PRODUCT_MAXIMUM_DISCOUNT_RATE}
                  onChange={(value) => {
                    setData({
                      path: ['discountRate', 'startRate'],
                      value: [setStartProductValueRange(value, discountRate.endRate[0])],
                    });
                  }}
                  addonAfter={'%'}
                />

                <span className="dash">~</span>

                <InputNumber
                  value={discountRate.endRate[0]}
                  placeholder={0}
                  min={10}
                  max={PRODUCT_MAXIMUM_DISCOUNT_RATE}
                  onChange={(value) => {
                    setData({
                      path: ['discountRate', 'endRate'],
                      value: [setEndProductValueRange(value, discountRate.startRate[0], PRODUCT_MAXIMUM_DISCOUNT_RATE)],
                    });
                  }}
                  addonAfter={'%'}
                />
              </div>
            ) : null}
          </Fragment>
        }
      />
      {discountDetailToggle ? (
        <Fragment>
          <BaseForm
            label={'할인가'}
            content={
              <div className="select-search-wrap">
                {/*범위 검색*/}
                <Select
                  className="select-search-option"
                  value={discountPrice.priceIndex}
                  list={PRODUCT_SEARCH_PRICE_SELECT_LIST}
                  onChange={(value) => {
                    const priceData = filter(PRODUCT_SEARCH_PRICE_SELECT_LIST, (item) => item.id === value)[0];

                    setData({
                      path: ['discountPrice'],
                      value: {
                        priceIndex: priceData.id,
                        startPrice: priceData.id ? priceData.value.startPrice : null,
                        endPrice: priceData.id ? priceData.value.endPrice : null,
                      },
                    });
                  }}
                />

                {/*직접 입력*/}
                {discountPrice.priceIndex === 6 ? (
                  <div className="input-search-wrap">
                    <InputNumber
                      value={discountPrice.startPrice}
                      placeholder={0}
                      min={0}
                      max={PRODUCT_MAXIMUM_PRICE - 1}
                      onChange={(value) => {
                        setData({
                          path: ['discountPrice', 'startPrice'],
                          value: setStartProductValueRange(value, discountPrice.endPrice),
                        });
                      }}
                      addonAfter={'원'}
                    />

                    <span className="dash">~</span>

                    <InputNumber
                      value={discountPrice.endPrice}
                      placeholder={0}
                      min={9999}
                      max={PRODUCT_MAXIMUM_PRICE}
                      onChange={(value) => {
                        setData({
                          path: ['discountPrice', 'endPrice'],
                          value: setEndProductValueRange(value, discountPrice.startPrice, PRODUCT_MAXIMUM_PRICE),
                        });
                      }}
                      addonAfter={'원'}
                    />
                  </div>
                ) : null}
              </div>
            }
          />

          <BaseForm
            label={'할인설정'}
            content={
              <div className="search-section">
                <ButtonList
                  current={discountType.value}
                  list={PRODUCT_DISCOUNT_BUTTON_LIST}
                  onClick={(data) => {
                    setData({
                      path: ['discountType'],
                      value: {
                        discountIndex: data.id,
                        value: !isNil(data.value) ? data.value : null,
                      },
                    });
                  }}
                />
              </div>
            }
          />
        </Fragment>
      ) : null}

      <BaseForm
        className="category-search"
        label="카테고리"
        content={
          <Select
            mode="multiple"
            showSearch
            isTable
            optionFilterProp="children"
            dropdownClassName="category-drop-down"
            list={categorySelectList}
            value={productCategory.list}
            onChange={(value) =>
              setData({
                path: ['productCategory', 'list'],
                value,
              })
            }
          />
        }
      />

      <BaseForm
        className="tag-search"
        label="태그 그룹"
        content={
          <Select
            mode="multiple"
            showSearch
            isTable
            optionFilterProp="children"
            list={tagGroupList}
            value={productTag.tagGroupList}
            onChange={(tagGroupId) =>
              setData({
                path: ['productTag', 'tagGroupList'],
                value: tagGroupId,
              })
            }
          />
        }
      />

      <BaseForm
        className="tag-search"
        label="태그"
        content={
          <Select
            mode="multiple"
            showSearch
            isTable
            optionFilterProp="children"
            list={tagList}
            value={productTag.tagList}
            onChange={(tagId) =>
              setData({
                path: ['productTag', 'tagList'],
                value: tagId,
              })
            }
          />
        }
      />
    </SearchBox>
  );
};

export default ProductListSearchBox;
